// Stripe Appearance API: match the Payment Element to the partner theme.
// The iframe contents cannot be styled with our CSS, only through these variables.
const cssVar = (name, fallback) => {
    try {
        const value = getComputedStyle(document.documentElement).getPropertyValue(name)
        return value && value.trim() ? value.trim() : fallback
    } catch (e) {
        return fallback
    }
}

// The customer portal uses the system font stack (index.css), available in the iframe too: nothing to load
export const stripeFonts = []

// Measure the portal's real field styles (the same markup as "Name on Card": Form.Label
// .paymentCardDetials.small + Form.Control .form-control) so the Stripe fields match them exactly,
// whatever the global CSS overrides are. Falls back to Bootstrap values if measuring fails.
const measurePortalField = () => {
    const fallback = {
        labelSize: '14px', labelWeight: '400', labelColor: '#212529', labelGap: '8px',
        inputSize: '14px', inputColor: '#212529', padY: '12px', padX: '14px',
        radius: '6px', border: '#dee2e6', rowGap: '16px'
    }
    try {
        const probe = document.createElement('div')
        probe.setAttribute('aria-hidden', 'true')
        probe.style.cssText = 'position:absolute;left:-9999px;top:0;width:320px;visibility:hidden;pointer-events:none'
        probe.innerHTML = '<div class="form-group"><label class="form-label paymentCardDetials small">Name</label><input class="form-control" type="text" /></div>'
        document.body.appendChild(probe)
        const label = getComputedStyle(probe.querySelector('label'))
        const input = getComputedStyle(probe.querySelector('input'))
        const fontSize = parseFloat(input.fontSize) || 14
        const height = parseFloat(input.height) || 0
        const borders = (parseFloat(input.borderTopWidth) || 0) + (parseFloat(input.borderBottomWidth) || 0)
        // Stripe inputs get line-height 1.5, so the vertical padding is derived from the measured height
        const padY = height > 0 ? Math.max(6, Math.round((height - borders - fontSize * 1.5) / 2)) : 12
        const measured = {
            labelSize: label.fontSize || fallback.labelSize,
            labelWeight: label.fontWeight || fallback.labelWeight,
            labelColor: label.color || fallback.labelColor,
            labelGap: `${Math.max(4, parseFloat(label.marginBottom) || 8)}px`,
            inputSize: `${fontSize}px`,
            inputColor: input.color || fallback.inputColor,
            padY: `${padY}px`,
            padX: `${Math.max(8, Math.round(parseFloat(input.paddingLeft) || 14))}px`,
            radius: input.borderTopLeftRadius || fallback.radius,
            border: input.borderTopColor || fallback.border,
            rowGap: '16px'
        }
        document.body.removeChild(probe)
        return measured
    } catch (e) {
        return fallback
    }
}

// Focus ring = partner colour, like Bootstrap's .form-control:focus
export const getStripeAppearance = () => {
    const field = measurePortalField()
    const primary = cssVar('--bs-primary', '#202d5d')
    const primaryRgb = cssVar('--bs-primary-rgb', '32,45,93')
    const danger = cssVar('--bs-danger', '#dc3545')
    const text = field.inputColor
    const muted = '#6c757d'
    const border = field.border
    return {
        theme: 'stripe',
        labels: 'above',
        variables: {
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
            fontSizeBase: field.inputSize,
            fontWeightNormal: '400',
            fontWeightMedium: '500',
            colorPrimary: primary,
            colorText: text,
            colorTextSecondary: muted,
            colorTextPlaceholder: '#9aa0a6',
            colorDanger: danger,
            colorBackground: '#ffffff',
            borderRadius: field.radius,
            spacingUnit: '4px',
            gridRowSpacing: field.rowGap,
            gridColumnSpacing: '12px'
        },
        rules: {
            '.Label': {
                fontSize: field.labelSize,
                fontWeight: field.labelWeight,
                color: field.labelColor,
                marginBottom: field.labelGap
            },
            '.Input': {
                fontSize: field.inputSize,
                lineHeight: '1.5',
                padding: `${field.padY} ${field.padX}`,
                border: `1px solid ${border}`,
                boxShadow: 'none'
            },
            '.Input:hover': { borderColor: '#c4c9cf' },
            '.Input:focus': {
                borderColor: primary,
                boxShadow: `0 0 0 0.2rem rgba(${primaryRgb}, .25)`
            },
            '.Input--invalid': {
                borderColor: danger,
                boxShadow: 'none',
                color: text
            },
            '.Error': { fontSize: '13px', marginTop: '4px' },
            '.TermsText': { fontSize: '12px', lineHeight: '1.5', color: muted }
        }
    }
}

// Card fields only: Apple Pay / Google Pay live in the ParkEngage wallet tab, and Link
// (with its Bank tab and "save my information" sign-up) is turned off.
// address 'if_required' collects no billing address for cards; 'never' would force us to
// pass country / postal code ourselves in createConfirmationToken.
export const paymentElementOptions = {
    layout: 'tabs',
    wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
    fields: { billingDetails: { address: 'if_required' } }
}
